import {Directive, ElementRef, Input, AfterViewInit, OnDestroy} from "@angular/core";
import { Subscription } from "rxjs";
import { OrganizationService } from "src/app/services";
import { BrandingService } from "src/app/services/branding.service";

/**
 * Org / platinum whitelabel header branding.
 * When BrandingService.appBranding.primaryColor (and optional themeFont) is set
 * from the organization, this overrides default Elite navy toolbars.
 * Design-system token changes must not remove or bypass this path.
 */
@Directive({
  selector: '[brandingBackground]'
})
export class BrandingBackgroundDirective implements AfterViewInit, OnDestroy {

  public orgSub: Subscription;
  private observer: MutationObserver;

  @Input('brandingBackground') test = '';

  constructor(
    private el: ElementRef,
    private brandingService: BrandingService,
    private organizationService: OrganizationService
  ) {
  }

  ngOnInit(){
    this.setProperty();

    this.orgSub = this.organizationService.organizationSubject.subscribe(() => {
      this.setProperty();
    });
  }

  ngAfterViewInit() {
    // Ensure DOM is ready before applying styles to child elements
    setTimeout(() => {
      this.applyChildStyles();
      this.setupMutationObserver();
    }, 100);
  }  

  ngOnDestroy() {
    if(this.orgSub) {
      this.orgSub.unsubscribe()
    }
    if(this.observer) {
      this.observer.disconnect();
    }
  }

  setProperty() {
    if(this.brandingService.appBranding[this.test]) {
      this.el.nativeElement.style.setProperty('--background', this.brandingService.appBranding[this.test]);
      this.el.nativeElement.style.setProperty('background-color', this.brandingService.appBranding[this.test]);
    }
    // Only set theme font color if it's explicitly set
    const themeFont = this.brandingService.appBranding.themeFont;
    if (themeFont) {
      this.el.nativeElement.style.setProperty('--color', themeFont);
      this.el.nativeElement.style.setProperty('color', themeFont);
      
      // Set Ionic-specific CSS variables for toolbar and title
      this.el.nativeElement.style.setProperty('--ion-text-color', themeFont);
      this.el.nativeElement.style.setProperty('--ion-toolbar-color', themeFont);
      
      // Apply to child elements
      this.applyChildStyles();
    } else {
      // Remove styles if themeFont is not set
      this.el.nativeElement.style.removeProperty('--color');
      this.el.nativeElement.style.removeProperty('color');
      this.el.nativeElement.style.removeProperty('--ion-text-color');
      this.el.nativeElement.style.removeProperty('--ion-toolbar-color');
    }
  }

  private setupMutationObserver() {
    // Watch for dynamically added buttons and icons
    this.observer = new MutationObserver(() => {
      this.applyChildStyles();
    });
    
    this.observer.observe(this.el.nativeElement, {
      childList: true,
      subtree: true
    });
  }

  private applyChildStyles() {
    const themeFont = this.brandingService.appBranding.themeFont;
    
    // Only apply styles if themeFont is set
    if (!themeFont) {
      return;
    }
    
    // Apply to ion-title elements
    const titleElements = this.el.nativeElement.querySelectorAll('ion-title');
    titleElements.forEach((title: any) => {
      title.style.setProperty('--color', themeFont);
      title.style.setProperty('color', themeFont);
      // Try to access shadow DOM if it exists
      if (title.shadowRoot) {
        const titleContent = title.shadowRoot.querySelector('.toolbar-title') || title.shadowRoot.querySelector('div');
        if (titleContent) {
          (titleContent as HTMLElement).style.setProperty('color', themeFont);
        }
      }
    });
    
    // Apply to ion-button elements
    const buttonElements = this.el.nativeElement.querySelectorAll('ion-button');
    buttonElements.forEach((button: any) => {
      const buttonColor = button.getAttribute('color');
      
      // Override Ionic color variables - if color="white", only override the text color (contrast), not the background
      if (buttonColor === 'white') {
        // Only override the contrast (text color), keep the white background
        button.style.setProperty('--ion-color-white-contrast', themeFont);
      }
      
      // Set text color variables (but not background for colored buttons)
      button.style.setProperty('--color', themeFont);
      button.style.setProperty('color', themeFont);
      // Only override base/contrast if it's not a colored button (to avoid changing backgrounds)
      if (!buttonColor || buttonColor === 'white') {
        button.style.setProperty('--ion-color-contrast', themeFont);
      }
      
      // Apply to shadow DOM if it exists
      if (button.shadowRoot) {
        const buttonContent = button.shadowRoot.querySelector('.button-native') || button.shadowRoot.querySelector('button');
        if (buttonContent) {
          // Only set text color properties, not background
          (buttonContent as HTMLElement).style.setProperty('--color', themeFont);
          (buttonContent as HTMLElement).style.setProperty('color', themeFont);
          
          // For white buttons, also set the contrast variable in shadow DOM
          if (buttonColor === 'white') {
            (buttonContent as HTMLElement).style.setProperty('--ion-color-white-contrast', themeFont);
          }
          
          // Target all child elements including text nodes (text color only)
          const allChildren = buttonContent.querySelectorAll('*');
          allChildren.forEach((el: HTMLElement) => {
            el.style.setProperty('color', themeFont);
          });
        }
        
        // Inject a style tag to ensure all text and slotted content gets the color (text only, preserve background)
        const existingStyle = button.shadowRoot.querySelector('style[data-theme-font]');
        const styleContent = buttonColor === 'white' 
          ? `
            .button-native {
              color: ${themeFont} !important;
              --color: ${themeFont} !important;
              --ion-color-white-contrast: ${themeFont} !important;
            }
            .button-native * {
              color: ${themeFont} !important;
            }
            ::slotted(*) {
              color: ${themeFont} !important;
            }
          `
          : `
            .button-native {
              color: ${themeFont} !important;
              --color: ${themeFont} !important;
            }
            .button-native * {
              color: ${themeFont} !important;
            }
            ::slotted(*) {
              color: ${themeFont} !important;
            }
          `;
        
        if (!existingStyle) {
          const styleElement = document.createElement('style');
          styleElement.setAttribute('data-theme-font', 'true');
          styleElement.textContent = styleContent;
          button.shadowRoot.appendChild(styleElement);
        } else {
          existingStyle.textContent = styleContent;
        }
      }
      
      // Apply to icons inside buttons (including in shadow DOM)
      this.applyIconStyles(button, themeFont);
    });
    
    // Apply to standalone ion-icon elements
    const iconElements = this.el.nativeElement.querySelectorAll('ion-icon');
    iconElements.forEach((icon: any) => {
      this.applyIconStyles(icon, themeFont);
    });
  }

  private hexToRgb(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '255, 255, 255';
  }

  private applyIconStyles(element: any, themeFont: string) {
    // Apply to the icon element itself
    element.style.setProperty('--color', themeFont);
    element.style.setProperty('color', themeFont);
    
    // Access shadow DOM for ion-icon
    if (element.shadowRoot) {
      const iconContent = element.shadowRoot.querySelector('svg') || element.shadowRoot.querySelector('.icon-inner');
      if (iconContent) {
        (iconContent as HTMLElement).style.setProperty('fill', themeFont);
        (iconContent as HTMLElement).style.setProperty('color', themeFont);
      }
      
      // Also set CSS variables on shadow root
      const style = element.shadowRoot.querySelector('style');
      if (!style || !style.textContent?.includes('--color')) {
        const styleElement = document.createElement('style');
        styleElement.textContent = `:host { --color: ${themeFont} !important; color: ${themeFont} !important; }`;
        element.shadowRoot.appendChild(styleElement);
      }
    }
    
    // Also query for nested icons (in case they're not direct children)
    const nestedIcons = element.querySelectorAll('ion-icon');
    nestedIcons.forEach((icon: any) => {
      this.applyIconStyles(icon, themeFont);
    });
  }
}
